/**
 * Integrytas Group - Identity & Access Management (IAM)
 * Page: IAM.PROVIDERS
 *
 * Configuration: identity providers (federated on GoTrue), claim/domain→role
 * mappings, and downstream client-app registrations. Secrets live in GoTrue's
 * server config; only non-secret metadata is stored here.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'IAM.PROVIDERS';

interface ProviderRow { iam_provider_id: number; internal_code: string; name: string; provider_type: string; gotrue_provider: string | null; auto_provision: 'Y' | 'N'; default_role_id: number | null; is_active: 'Y' | 'N' }
interface MappingRow { iam_role_mapping_id: number; iam_provider_id: number | null; match_type: string; match_key: string | null; match_value: string; role_id: number; is_active: 'Y' | 'N' }
interface ClientRow { iam_client_app_id: number; internal_code: string; name: string; allowed_audiences: unknown; allowed_scopes: unknown; is_active: 'Y' | 'N' }
interface RoleLite { role_id: number; internal_code: string }

const PROVIDER_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code',    header: 'Code',         defaultWidth: 180 },
  { key: 'name',             header: 'Name',         defaultWidth: 170 },
  { key: 'provider_type',    header: 'Type',         defaultWidth: 110 },
  { key: 'gotrue_provider',  header: 'GoTrue Key',   defaultWidth: 150 },
  { key: 'default_role',     header: 'Default Role', defaultWidth: 160 },
  { key: 'auto_provision',   header: 'JIT',          defaultWidth: 70  },
  { key: 'is_active',        header: 'Active',       defaultWidth: 70  },
];

export interface IamProviderManagerPageProps { session: AuthSession }

export function IamProviderManagerPage({ session }: IamProviderManagerPageProps) {
  return (
    <PersonalizationProvider pageCode="IAM.PROVIDERS" session={session}>
      <IamProviderManagerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function IamProviderManagerPageInner({ session }: IamProviderManagerPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [mappings, setMappings] = useState<MappingRow[]>([]);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [roles, setRoles] = useState<RoleLite[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets for the providers list.
  const [facets, setFacets] = useState<{ provider_type: string[]; default_role: string[]; auto_provision: string[]; is_active: string[] }>(
    { provider_type: [], default_role: [], auto_provision: [], is_active: ['Y'] },
  );

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [p, m, c, r] = await Promise.all([
        apiDb.from('iam_provider').select('iam_provider_id, internal_code, name, provider_type, gotrue_provider, auto_provision, default_role_id, is_active').order('internal_code'),
        apiDb.from('iam_role_mapping').select('iam_role_mapping_id, iam_provider_id, match_type, match_key, match_value, role_id, is_active').order('iam_role_mapping_id'),
        apiDb.from('iam_client_app').select('iam_client_app_id, internal_code, name, allowed_audiences, allowed_scopes, is_active').order('internal_code'),
        apiDb.from('csm_role_master').select('role_id, internal_code').order('internal_code'),
      ]);
      if (p.error) throw p.error;
      setProviders((p.data ?? []) as ProviderRow[]); setMappings((m.data ?? []) as MappingRow[]); setClients((c.data ?? []) as ClientRow[]); setRoles((r.data ?? []) as RoleLite[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const roleCodeOf = (id: number | null) => (id == null ? '' : (roles.find(r => r.role_id === id)?.internal_code ?? ''));

  // §17.B13 related-children disclosure — a provider's role mappings, built from the
  // already-loaded `mappings`. The match value the admin entered (domain/claim/group)
  // is the human label; the match type and target role code ride along in the title.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    {
      key: 'mappings',
      table: 'iam_role_mapping', pkColumn: 'iam_role_mapping_id',
      title: t('iam.related.mappings', 'Role Mappings'),
      items: mappings
        .filter(m => m.iam_provider_id === id)
        .map(m => ({
          id: m.iam_role_mapping_id,
          label: m.match_value || m.match_type,
          title: `${m.match_type}${m.match_key ? ` ${m.match_key}` : ''} → ${roleCodeOf(m.role_id)}`,
        })),
    },
  ];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort().map(v => ({ value: v, label: v }));
    return {
      provider_type: uniq(providers.map(p => p.provider_type)),
      default_role:  uniq(providers.map(p => roleCodeOf(p.default_role_id))),
      auto_provision: [{ value: 'Y', label: t('iam.facet.jit_on', 'JIT on') }, { value: 'N', label: t('iam.facet.jit_off', 'JIT off') }],
      is_active:     [{ value: 'Y', label: t('iam.facet.active', 'Active') }, { value: 'N', label: t('iam.facet.inactive', 'Inactive') }],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers, roles, t]);

  const facetsActive = facets.provider_type.length + facets.default_role.length + facets.auto_provision.length + facets.is_active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ provider_type: [], default_role: [], auto_provision: [], is_active: [] }); };

  const filteredProviders = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matchesFacets = (p: ProviderRow) =>
      (facets.provider_type.length === 0  || facets.provider_type.includes(p.provider_type)) &&
      (facets.default_role.length === 0   || facets.default_role.includes(roleCodeOf(p.default_role_id))) &&
      (facets.auto_provision.length === 0 || facets.auto_provision.includes(p.auto_provision)) &&
      (facets.is_active.length === 0      || facets.is_active.includes(p.is_active));
    return providers.filter(p =>
      (!q || p.internal_code.toLowerCase().includes(q) || (p.name ?? '').toLowerCase().includes(q))
      && matchesFacets(p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers, filter, facets, roles]);

  const addProvider = async () => { await routedInsert(session, 'iam_provider', { internal_code: `IAM.PROV_${Date.now()}`, name: 'New provider', provider_type: 'OAUTH', auto_provision: 'N', is_active: 'N' }); await reload(); };
  const saveProvider = async (p: ProviderRow) => { await routedUpdate(session, 'iam_provider', 'iam_provider_id', p.iam_provider_id, { name: p.name, provider_type: p.provider_type, gotrue_provider: p.gotrue_provider, auto_provision: p.auto_provision, default_role_id: p.default_role_id, is_active: p.is_active }); };
  const delProvider = async (id: number) => { await routedDelete(session, 'iam_provider', 'iam_provider_id', id); setProviders(ps => ps.filter(x => x.iam_provider_id !== id)); };

  const addMapping = async () => { await routedInsert(session, 'iam_role_mapping', { match_type: 'DOMAIN', match_value: '', role_id: roles[0]?.role_id ?? null, is_active: 'Y' }); await reload(); };
  const saveMapping = async (m: MappingRow) => { await routedUpdate(session, 'iam_role_mapping', 'iam_role_mapping_id', m.iam_role_mapping_id, { iam_provider_id: m.iam_provider_id, match_type: m.match_type, match_key: m.match_key || null, match_value: m.match_value, role_id: m.role_id, is_active: m.is_active }); };
  const delMapping = async (id: number) => { await routedDelete(session, 'iam_role_mapping', 'iam_role_mapping_id', id); setMappings(ms => ms.filter(x => x.iam_role_mapping_id !== id)); };

  const addClient = async () => { await routedInsert(session, 'iam_client_app', { internal_code: `IAM.CLIENT_${Date.now()}`, name: 'New client', allowed_audiences: [], allowed_scopes: [], is_active: 'Y' }); await reload(); };
  const saveClient = async (c: ClientRow, audText: string, scoText: string) => {
    let aud: unknown; let sco: unknown;
    try { aud = JSON.parse(audText); sco = JSON.parse(scoText); } catch { setError(t('iam.err.json_arrays', 'Audiences/scopes must be JSON arrays.')); return; }
    await routedUpdate(session, 'iam_client_app', 'iam_client_app_id', c.iam_client_app_id, { name: c.name, allowed_audiences: aud, allowed_scopes: sco, is_active: c.is_active });
  };
  const delClient = async (id: number) => { await routedDelete(session, 'iam_client_app', 'iam_client_app_id', id); setClients(cs => cs.filter(x => x.iam_client_app_id !== id)); };


  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('iam.title', 'Identity Providers')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('iam.filter_placeholder', 'Filter providers…')}
          className="h-7 w-60 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter label={t('iam.col.provider_type', 'Type')}    options={facetOptions.provider_type} selected={facets.provider_type} onChange={v => setFacets(f => ({ ...f, provider_type: v }))} />
        <FacetFilter label={t('iam.col.default_role', 'Default Role')} options={facetOptions.default_role} selected={facets.default_role} onChange={v => setFacets(f => ({ ...f, default_role: v }))} />
        <FacetFilter label={t('iam.col.auto_provision', 'JIT')}     options={facetOptions.auto_provision} selected={facets.auto_provision} onChange={v => setFacets(f => ({ ...f, auto_provision: v }))} />
        <FacetFilter label={t('iam.col.is_active', 'Active')}        options={facetOptions.is_active} selected={facets.is_active} onChange={v => setFacets(f => ({ ...f, is_active: v }))} />
        <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('iam.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('iam.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <ErrorBanner
          message={t('iam.error_banner', "Couldn't complete the last identity-provider operation.")}
          detail={error}
          retry_label={t('common.retry', 'Retry')}
          on_retry={() => void reload()}
          details_label={t('common.details', 'Details')}
        />
      )}
      <div className="flex-1 overflow-auto p-3">
        <Section title={t('iam.section.providers', 'Providers')} onAdd={addProvider}>
          <PersonalizedGrid componentKey="grid" columns={PROVIDER_GRID_COLS}>
            {(visibleCols, widths) => (
              <table className={GRID_TABLE_CLASS}>
                <thead className="bg-csm-surface-alt text-csm-2xs uppercase tracking-wide text-csm-watermark">
                  <tr>
                    <th className="w-7 border-b border-csm-border px-2 py-1" title={t('iam.related.header', 'Related records')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('iam.col.' + col.key, col.header)}</th>
                    ))}
                    <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('iam.col.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProviders.map(p => {
                    const i = providers.indexOf(p);
                    return (
                      <Fragment key={p.iam_provider_id}>
                      <tr>
                        <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                          <RelatedExpandButton open={detail.isOpen(p.iam_provider_id)} hasChildren={hasRelated(p.iam_provider_id)} onToggle={() => detail.toggle(p.iam_provider_id)} expandLabel={t('iam.related.expand', 'Show related records')} collapseLabel={t('iam.related.collapse', 'Hide related')} tooltip={t('iam.related.tooltip', 'Show related records')} />
                        </td>
                        {visibleCols.map(col => (
                          <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 align-middle"
                              style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                            {col.key === 'internal_code'   && <span className="font-mono text-csm-2xs text-csm-ink-soft">{p.internal_code}</span>}
                            {col.key === 'name'            && <input value={p.name} onChange={e => setProviders(ps => ps.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} onBlur={() => void saveProvider(providers[i])} placeholder={t('iam.ph.name', 'name')} className="h-7 w-full rounded border border-csm-border px-1 text-csm-xs" />}
                            {col.key === 'provider_type'   && <select value={p.provider_type} onChange={e => setProviders(ps => ps.map((x, j) => j === i ? { ...x, provider_type: e.target.value } : x))} onBlur={() => void saveProvider(providers[i])} className="h-7 w-full rounded border border-csm-border px-1 text-csm-xs">{['OAUTH', 'OIDC', 'SAML', 'PASSWORD'].map(t => <option key={t} value={t}>{t}</option>)}</select>}
                            {col.key === 'gotrue_provider' && <input value={p.gotrue_provider ?? ''} onChange={e => setProviders(ps => ps.map((x, j) => j === i ? { ...x, gotrue_provider: e.target.value } : x))} onBlur={() => void saveProvider(providers[i])} placeholder={t('iam.ph.gotrue_key', 'gotrue key (google…)')} className="h-7 w-full rounded border border-csm-border px-1 font-mono text-csm-xs" />}
                            {col.key === 'default_role'    && (
                              <FilteredModalSingleSelect
                                label={t('iam.col.default_role', 'Default Role')}
                                options={roles.map(r => ({ internal_code: String(r.role_id), display_text: r.internal_code }))}
                                selectedCode={p.default_role_id == null ? null : String(p.default_role_id)}
                                onChange={(code) => {
                                  const v = code ? Number(code) : null;
                                  setProviders(ps => ps.map((x, j) => j === i ? { ...x, default_role_id: v } : x));
                                  void saveProvider({ ...providers[i], default_role_id: v });
                                }}
                                clearLabel={t('iam.no_default_role', 'no default role')}
                              />
                            )}
                            {col.key === 'auto_provision'  && <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={p.auto_provision === 'Y'} onChange={e => { setProviders(ps => ps.map((x, j) => j === i ? { ...x, auto_provision: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveProvider(providers[i])} /> {t('iam.jit', 'JIT')}</label>}
                            {col.key === 'is_active'       && <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={p.is_active === 'Y'} onChange={e => { setProviders(ps => ps.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveProvider(providers[i])} /> {t('iam.on', 'on')}</label>}
                          </td>
                        ))}
                        <td className="border-b border-csm-border-soft px-2 py-1 text-right">{canDelete && (<button onClick={() => void delProvider(p.iam_provider_id)} className="text-csm-danger text-csm-xs">{t('iam.del', 'del')}</button>)}</td>
                      </tr>
                      {detail.isOpen(p.iam_provider_id) && (
                        <tr>
                          <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                            <RelatedChildrenPanel groups={buildRelatedGroups(p.iam_provider_id)} session={session} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                  {filteredProviders.length === 0 && !busy && (
                    <tr><td colSpan={visibleCols.length + 2} className="px-2 py-8 text-center">
                      <EmptyState
                        variant={anyFilterActive ? 'no-match' : 'no-data'}
                        title={t('iam.no_providers', 'No providers.')}
                        action={anyFilterActive ? undefined : canAdd && (<button onClick={() => void addProvider()} className="mt-2 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('iam.add', '+ Add')}</button>)}
                      />
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </Section>

        <Section title={t('iam.section.mappings', 'Role Mappings (domain / claim / group → role)')} onAdd={addMapping}>
          {mappings.map((m, i) => (
            <div key={m.iam_role_mapping_id} className="mb-1 flex flex-wrap items-center gap-1">
              <div className="w-36">
                <FilteredModalSingleSelect
                  label={t('iam.field.provider', 'Provider')}
                  options={providers.map(p => ({ internal_code: String(p.iam_provider_id), display_text: p.internal_code }))}
                  selectedCode={m.iam_provider_id == null ? null : String(m.iam_provider_id)}
                  onChange={(code) => {
                    const v = code ? Number(code) : null;
                    setMappings(ms => ms.map((x, j) => j === i ? { ...x, iam_provider_id: v } : x));
                    void saveMapping({ ...mappings[i], iam_provider_id: v });
                  }}
                  clearLabel={t('iam.all_providers', 'all providers')}
                />
              </div>
              <select value={m.match_type} onChange={e => setMappings(ms => ms.map((x, j) => j === i ? { ...x, match_type: e.target.value } : x))} onBlur={() => void saveMapping(mappings[i])} className="h-7 rounded border border-csm-border px-1 text-csm-xs">{['DOMAIN', 'CLAIM', 'GROUP'].map(mt => <option key={mt} value={mt}>{mt}</option>)}</select>
              {m.match_type !== 'DOMAIN' && <input value={m.match_key ?? ''} onChange={e => setMappings(ms => ms.map((x, j) => j === i ? { ...x, match_key: e.target.value } : x))} onBlur={() => void saveMapping(mappings[i])} placeholder={t('iam.ph.claim_key', 'claim key')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-xs" />}
              <input value={m.match_value} onChange={e => setMappings(ms => ms.map((x, j) => j === i ? { ...x, match_value: e.target.value } : x))} onBlur={() => void saveMapping(mappings[i])} placeholder={t('iam.ph.match_value', 'value (acme.com)')} className="h-7 w-36 rounded border border-csm-border px-1 font-mono text-csm-xs" />
              <span className="text-csm-2xs text-csm-watermark">→</span>
              <div className="w-36">
                <FilteredModalSingleSelect
                  label={t('iam.field.role', 'Role')}
                  options={roles.map(r => ({ internal_code: String(r.role_id), display_text: r.internal_code }))}
                  selectedCode={String(m.role_id)}
                  onChange={(code) => {
                    const v = code ? Number(code) : m.role_id;
                    setMappings(ms => ms.map((x, j) => j === i ? { ...x, role_id: v } : x));
                    void saveMapping({ ...mappings[i], role_id: v });
                  }}
                  allowClear={false}
                />
              </div>
              {canDelete && (<button onClick={() => void delMapping(m.iam_role_mapping_id)} className="text-csm-danger text-csm-xs">{t('iam.del', 'del')}</button>)}
            </div>
          ))}
        </Section>

        <Section title={t('iam.section.clients', 'Downstream Client Apps')} onAdd={addClient}>
          {clients.map((c, i) => (
            <div key={c.iam_client_app_id} className="mb-1 flex flex-wrap items-center gap-1">
              <span className="w-44 font-mono text-csm-2xs text-csm-ink-soft">{c.internal_code}</span>
              <input value={c.name} onChange={e => setClients(cs => cs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} onBlur={() => void saveClient(clients[i], JSON.stringify(clients[i].allowed_audiences ?? []), JSON.stringify(clients[i].allowed_scopes ?? []))} placeholder={t('iam.ph.name', 'name')} className="h-7 w-40 rounded border border-csm-border px-1 text-csm-xs" />
              <input defaultValue={JSON.stringify(c.allowed_audiences ?? [])} onBlur={e => void saveClient(clients[i], e.target.value, JSON.stringify(clients[i].allowed_scopes ?? []))} placeholder={t('iam.ph.audiences', 'audiences ["app"]')} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-2xs" />
              <input defaultValue={JSON.stringify(c.allowed_scopes ?? [])} onBlur={e => void saveClient(clients[i], JSON.stringify(clients[i].allowed_audiences ?? []), e.target.value)} placeholder={t('iam.ph.scopes', 'scopes ["read"]')} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-2xs" />
              <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={c.is_active === 'Y'} onChange={e => { setClients(cs => cs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveClient(clients[i], JSON.stringify(clients[i].allowed_audiences ?? []), JSON.stringify(clients[i].allowed_scopes ?? []))} /> {t('iam.on', 'on')}</label>
              {canDelete && (<button onClick={() => void delClient(c.iam_client_app_id)} className="text-csm-danger text-csm-xs">{t('iam.del', 'del')}</button>)}
            </div>
          ))}
        </Section>
        <div className="text-csm-2xs text-csm-watermark">{t('iam.note', 'Note: enable each provider in GoTrue first (server config holds the client secret). Roles map on login via iam_resolve_login.')}</div>
      </div>
    </div>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 rounded border border-csm-border bg-white p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{title}</span>
        <button onClick={onAdd} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('iam.add', '+ Add')}</button>
      </div>
      {children}
    </div>
  );
}
